import { createContext, useContext, type ReactNode } from 'react';

/**
 * Link handling is the one piece of environment-specific behavior the
 * renderer always delegates to the host:
 *   - Kaleidoscope (web): in-app navigation / standard browser navigation.
 *   - Bizy Desktop (Electron): `shell.openExternal`.
 *
 * The default opens the href in a new browser tab, which is a safe
 * baseline when no host handler is provided.
 */
export type BizyLinkHandler = (href: string) => void;

const defaultHandler: BizyLinkHandler = (href) => {
	if (typeof window !== 'undefined' && href) {
		window.open(href, '_blank', 'noopener,noreferrer');
	}
};

const BizyLinkContext = createContext<BizyLinkHandler>(defaultHandler);

export function BizyLinkProvider({ onLinkClick, children }: { onLinkClick?: BizyLinkHandler; children: ReactNode }) {
	return <BizyLinkContext.Provider value={onLinkClick ?? defaultHandler}>{children}</BizyLinkContext.Provider>;
}

export function useBizyLinkClick(): BizyLinkHandler {
	return useContext(BizyLinkContext);
}
